import { PlayerRef } from '@remotion/player';
import {getWorkAssetDrag,setWorkAssetDrag,WORK_ASSET_DRAG_TYPE,prepareWorkAsset} from '@waka/compat/work-asset-library';
import {commitIncomingMedia} from '@waka/compat/commit-incoming-media';
import {captureInsertionTarget} from '@waka/compat/incoming-media';
import {readBgmFile} from '@waka/compat/bgm-file';
import {catalogSoundEffectSource} from '@waka/compat/catalog-sound-effect-source';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AbsoluteFill } from 'remotion';
import { addAsset, DropPosition } from './assets/add-asset';
import { EditorStarterItem } from './items/item-type';
import { TrackType, UndoableState } from './state/types';
import {
  captureExternalTarget,
  previewIncomingPlacement,
  isIncomingPreviewCurrent,
} from '@waka/compat/incoming-preview';
import { snapMoveEdges } from '@waka/compat/move-snapping';
import { hasPendingTimelineEdit } from '@waka/compat/edit-ownership';
import {
  collectSnapPoints,
  type SnapPoint,
} from './timeline/utils/snap-points';
import { addAsset as addAssetWkwk } from './assets/add-asset-wkwk';
import { classifyUploadFailure } from '@/shared/utils/upload-error-classifier';
import { showUploadFailureToast } from '@/shared/utils/upload-failure-toast';
import { useTranslations } from 'next-intl';
import { CANVAS_PADDING } from './constants';
import { CanvasSizeContext } from './canvas/canvas-size';
import {
  FEATURE_DROP_ASSETS_ON_CANVAS,
  FEATURE_DROP_ASSETS_ON_TIMELINE,
} from './flags';
import { PreviewSizeContext } from './preview-size';
import { calculateScale } from './utils/calculate-canvas-transformation';
import {
  useBrandTemplateId,
  useCurrentStateAsRef,
  useFps,
  useWriteContext,
} from './utils/use-context';
import { BRAND_TEMPLATE_PREVIEW_DURATION_SECONDS } from '@/shared/constants/brand-template';
import {
  ASSET_DRAG_TYPE,
  AssetDragData,
  getCurrentDragData,
  setCurrentDragData,
} from './editor-property-panel/ui/contents/asset/AssetContentItem';
import {
  BGM_DRAG_TYPE,
  BgmDragData,
  getCurrentBgmDragData,
  setCurrentBgmDragData,
} from './editor-property-panel/ui/contents/audio/bgm-drag-data';
import { CLIP_TYPES, ClipType } from '@/shared/apis/editor/clip-types';
import {
  DtoContentType,
  DtoExtensionReverse,
  DtoProjectType,
} from '@/shared/constants';
import { toast } from '@crabs-ai/ds-react';
import { useAssetDownloadProgress } from './contexts/asset-download-progress-context';
import { TIMELINE_HORIZONTAL_PADDING } from './constants';
import {
  timelineScrollableContainerRef,
  timelineRightSide,
} from './utils/restore-scroll-after-zoom';
import { getVisibleFrames } from './utils/get-visible-frames';
import { getCompositionDuration } from './utils/get-composition-duration';
import { useAssetDropPreview } from './timeline/asset-drop-preview-provider';
import { useProjectTypeContext } from './contexts/project-type-context';
import {
  getAssetDropTrackTarget,
  AssetDropTrackTarget,
} from './utils/track-position-utils';
import { getZoneBoundaryIndex } from './utils/zone-utils';
import { remainOriginalTrackHeights } from './timeline/utils/drag/remain-original-track-heights';
import { type TimelineTrackAndLayout } from './timeline/utils/drag/calculate-track-heights';
import { usePropertyPanelContext } from './contexts/property-panel-context';
import {
  TRANSITION_DRAG_TYPE,
  getCurrentTransitionDragData,
  setCurrentTransitionDragData,
} from './editor-property-panel/ui/contents/transition/transition-drag-data';
import {
  ANIMATION_DRAG_TYPE,
  getCurrentAnimationDragData,
  setCurrentAnimationDragData,
} from './editor-property-panel/ui/contents/animation/animation-drag-data';
import { useTransitionDropPreview } from './timeline/transition-drop-preview-provider';
import { useAnimationDropPreview } from './timeline/animation-drop-preview-provider';
import {
  findNearestTransitionEdgeWithSortedItems,
  getTrackIndexFromY,
  TransitionEdge,
  buildSortedItemsByTrack,
} from './timeline/utils/transition-edge-utils';
import { TIMELINE_HEADER_HEIGHT } from './constants';
import { addTransition } from './state/actions/add-transition';
import { addAnimation, canApplyAnimation } from './state/actions/add-animation';
import { AnimationDropPreviewState } from './timeline/animation-drop-preview-provider';
import { addBgmAssetToTimeline } from './editor-property-panel/ui/contents/audio/add-bgm-asset-to-timeline';

// 드래그 중 계산된 위치 정보를 캐싱하기 위한 타입
interface DragPositionCache {
  baseDocument: UndoableState;
  baseLinkingEnabled: boolean;
  baseSnappingEnabled: boolean;
  frame: number;
  trackTarget: AssetDropTrackTarget;
  isAvailable: boolean;
}

// 드래그 시작 시 한 번만 계산하여 캐싱하는 정적 데이터
interface DragStaticCache {
  // 컴포지션 정보
  compositionDuration: number;
  visibleFrames: number;
  timelineWidth: number;
  // 트랙 레이아웃
  tracksLayout: TimelineTrackAndLayout[];
  // 트랙별 정렬된 아이템 (트랜지션 edge 탐색 최적화용)
  sortedItemsByTrack: Map<number, EditorStarterItem[]>;
  // 원본 데이터 참조 (변경 감지용)
  tracksRef: TrackType[];
  itemsRef: Record<string, EditorStarterItem>;
}

export const DropHandler: React.FC<{
  children: React.ReactNode;
  playerRef: React.RefObject<PlayerRef | null>;
  compositionHeight: number;
  compositionWidth: number;
  context: 'canvas' | 'timeline';
}> = ({
  children,
  playerRef,
  compositionHeight,
  compositionWidth,
  context,
}) => {
  const size = useContext(CanvasSizeContext);
  const { size: previewSize } = useContext(PreviewSizeContext);
  const { projectType: contextProjectType } = useProjectTypeContext();
  const resolvedProjectType = contextProjectType ?? DtoProjectType.spotlight;
  const timelineWriteContext = useWriteContext();
  const stateAsRef = useCurrentStateAsRef();
  const { fps } = useFps();
  const { brandTemplateId } = useBrandTemplateId();
  const isBrandTemplateEditor = !!brandTemplateId;
  const [isDragOver, setIsDragOver] = useState(false);
  const [isAudioDragOnCanvas, setIsAudioDragOnCanvas] = useState(false);
  const { fetchAssetWithProgress } = useAssetDownloadProgress();
  const { setPreviewState } = useAssetDropPreview();
  const { setPreviewState: setTransitionPreviewState } =
    useTransitionDropPreview();
  const { setPreviewState: setAnimationPreviewState } =
    useAnimationDropPreview();
  const { openAssetPanel } = usePropertyPanelContext();
  const t = useTranslations();

  // 드래그 위치 캐시: onDrop에서 재계산 없이 사용
  const dragPositionCacheRef = useRef<DragPositionCache | null>(null);
  // 트랜지션 드래그 위치 캐시
  const transitionEdgeCacheRef = useRef<TransitionEdge | null>(null);
  // 애니메이션 드래그 대상 캐시
  const animationTargetCacheRef = useRef<AnimationDropPreviewState | null>(
    null,
  );
  // 스로틀링을 위한 마지막 업데이트 시간
  const lastDragUpdateRef = useRef<number>(0);
  const DRAG_UPDATE_THROTTLE_MS = 16; // ~60fps
  // 드롭 처리 중 플래그 (중복 드롭 방지)
  const isProcessingDropRef = useRef<boolean>(false);

  // 드래그 시작 시 정적 데이터 캐시 (성능 최적화)
  const dragStaticCacheRef = useRef<DragStaticCache | null>(null);

  // 이전 프리뷰 상태 캐시 (불필요한 setState 방지)
  const lastAssetPreviewRef = useRef<{
    key: string;
    document: UndoableState;
  } | null>(null);
  const externalDragCancelledRef = useRef(false);
  useEffect(() => {
    const clear = () => {
      setPreviewState(null);
      dragPositionCacheRef.current = null;
      dragStaticCacheRef.current = null;
      lastAssetPreviewRef.current = null;
      setIsDragOver(false);
      setIsAudioDragOnCanvas(false);
      setCurrentDragData(null);
      setCurrentBgmDragData(null);
    };
    const start = () => {
      externalDragCancelledRef.current = false;
    };
    const cancel = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      externalDragCancelledRef.current = true;
      clear();
    };
    window.addEventListener('dragstart', start);
    window.addEventListener('dragend', clear);
    window.addEventListener('keydown', cancel);
    return () => {
      window.removeEventListener('dragstart', start);
      window.removeEventListener('dragend', clear);
      window.removeEventListener('keydown', cancel);
    };
  }, [setPreviewState]);
  const lastTransitionPreviewRef = useRef<{
    edgeFrame: number;
    trackIndex: number;
  } | null>(null);

  // 정적 캐시 초기화 (드래그 시작 시 한 번만 호출)
  const initializeDragStaticCache = useCallback((): DragStaticCache | null => {
    const state = stateAsRef.current;
    const tracks = state.undoableState.tracks;
    const items = state.undoableState.items;

    const container = timelineScrollableContainerRef.current;
    if (!container) {
      return null;
    }

    const compositionDuration = getCompositionDuration(items, tracks);
    const visibleFrames = getVisibleFrames({
      fps,
      totalDurationInFrames: compositionDuration,
    });
    const timelineWidth =
      container.scrollWidth - TIMELINE_HORIZONTAL_PADDING * 2;

    const tracksLayout = remainOriginalTrackHeights({
      originalTracks: tracks,
      originalItems: items,
      newTracks: tracks,
      newItems: items,
    });

    const sortedItemsByTrack = buildSortedItemsByTrack(tracks, items);

    return {
      compositionDuration,
      visibleFrames,
      timelineWidth,
      tracksLayout,
      sortedItemsByTrack,
      tracksRef: tracks,
      itemsRef: items,
    };
  }, [fps, stateAsRef]);

  // 정적 캐시 가져오기 또는 생성 (변경 감지 포함)
  const getOrCreateDragStaticCache = useCallback((): DragStaticCache | null => {
    const state = stateAsRef.current;
    const currentTracks = state.undoableState.tracks;
    const currentItems = state.undoableState.items;

    // 캐시가 없거나 데이터가 변경된 경우 재생성
    const existingCache = dragStaticCacheRef.current;
    if (
      !existingCache ||
      existingCache.tracksRef !== currentTracks ||
      existingCache.itemsRef !== currentItems
    ) {
      const newCache = initializeDragStaticCache();
      dragStaticCacheRef.current = newCache;
      return newCache;
    }

    return existingCache;
  }, [initializeDragStaticCache, stateAsRef]);

  // 캐시를 사용하여 프레임 계산 (O(1) 연산)
  const calculateFrameFromDropPositionCached = useCallback(
    (clientX: number, cache: DragStaticCache): number => {
      const container = timelineScrollableContainerRef.current;
      if (!container) {
        return 0;
      }

      const containerRect = container.getBoundingClientRect();
      const scrollLeft = container.scrollLeft;

      // 드롭 위치를 타임라인 내 상대 위치로 변환
      const relativeX =
        clientX - containerRect.left + scrollLeft - TIMELINE_HORIZONTAL_PADDING;

      // 프레임으로 변환
      const frame = Math.max(
        0,
        Math.round((relativeX / cache.timelineWidth) * cache.visibleFrames),
      );

      return frame;
    },
    [],
  );

  // 드롭 X 위치를 프레임으로 변환하는 함수 (fallback 용도, onDrop에서 사용)
  const calculateFrameFromDropPosition = useCallback(
    (clientX: number): number => {
      const state = stateAsRef.current;
      const tracks = state.undoableState.tracks;
      const items = state.undoableState.items;
      const durationInFrames = getCompositionDuration(items, tracks);
      const visibleFrames = getVisibleFrames({
        fps,
        totalDurationInFrames: durationInFrames,
      });

      const container = timelineScrollableContainerRef.current;
      if (!container) {
        return 0;
      }

      const containerRect = container.getBoundingClientRect();
      const scrollLeft = container.scrollLeft;

      // 타임라인 너비 계산 (사이드 패널 제외)
      const timelineWidth =
        container.scrollWidth - TIMELINE_HORIZONTAL_PADDING * 2;

      // 드롭 위치를 타임라인 내 상대 위치로 변환
      const relativeX =
        clientX - containerRect.left + scrollLeft - TIMELINE_HORIZONTAL_PADDING;

      // 프레임으로 변환
      const frame = Math.max(
        0,
        Math.round((relativeX / timelineWidth) * visibleFrames),
      );

      return frame;
    },
    [fps, stateAsRef],
  );

  const onDragOver: React.DragEventHandler = useCallback(
    (e) => {
      if (externalDragCancelledRef.current) return;
      if (!FEATURE_DROP_ASSETS_ON_TIMELINE && context === 'timeline') {
        return;
      }

      if (!FEATURE_DROP_ASSETS_ON_CANVAS && context === 'canvas') {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'copy';
      setIsDragOver(true);

      // 캔버스 드래그 시 시각적 피드백
      if (context === 'canvas') {
        setIsAudioDragOnCanvas(false);
      }

      // 타임라인 에셋 드롭 프리뷰 업데이트
      if (context === 'timeline') {
        // 스로틀링: 너무 빈번한 업데이트 방지
        const now = Date.now();
        if (now - lastDragUpdateRef.current < DRAG_UPDATE_THROTTLE_MS) {
          return;
        }
        lastDragUpdateRef.current = now;

        // 정적 캐시 가져오기 또는 생성 (드래그 시작 시 한 번만 계산)
        const staticCache = getOrCreateDragStaticCache();
        if (!staticCache) {
          return;
        }

        // 전역 드래그 데이터 가져오기 (dragover에서는 dataTransfer.getData 사용 불가)
        const dragData = getWorkAssetDrag() ?? getCurrentBgmDragData() ?? getCurrentDragData();

        if (dragData) {
          // 캐시된 데이터를 사용하여 프레임 계산 (O(1))
          let frame = calculateFrameFromDropPositionCached(
            e.clientX,
            staticCache,
          );
          const durationMs =
            isBrandTemplateEditor &&
            dragData.contentType === DtoContentType.image
              ? BRAND_TEMPLATE_PREVIEW_DURATION_SECONDS * 1000
              : dragData.contentType === DtoContentType.image &&
                  !/\.(gif|webp)(?:$|[?#])/i.test(dragData.name)
                ? 2000
                : dragData.duration && dragData.duration > 0
                  ? dragData.duration
                  : 2000;
          const estimatedDuration =
            !(
              isBrandTemplateEditor &&
              dragData.contentType === DtoContentType.image
            ) &&
            !(dragData.duration && dragData.duration > 0) &&
            (dragData.contentType !== DtoContentType.image ||
              /\.(gif|webp)(?:$|[?#])/i.test(dragData.name));
          const durationInFrames = 'durationInFrames' in dragData && typeof dragData.durationInFrames==='number'
            ? dragData.durationInFrames : Math.max(
            1,
            dragData.contentType === DtoContentType.image &&
              /\.(gif|webp)(?:$|[?#])/i.test(dragData.name)
              ? Math.floor((durationMs / 1000) * fps)
              : Math.round((durationMs / 1000) * fps),
          );

          // 트랙 정보 (캐시된 참조 사용)
          const tracks = staticCache.tracksRef;
          const items = staticCache.itemsRef;
          const base = stateAsRef.current;
          let incomingSnapPoint: SnapPoint | null = null;
          if (
            base.isSnappingEnabled &&
            !e.altKey &&
            staticCache.timelineWidth > 0
          ) {
            const snapped = snapMoveEdges(
              frame,
              durationInFrames,
              collectSnapPoints({ tracks, items, excludeItemIds: [] }),
              (8 * staticCache.visibleFrames) / staticCache.timelineWidth,
            );
            frame = snapped.from;
            incomingSnapPoint = snapped.point;
          }

          // 마우스 Y 위치에서 트랙 타겟 결정 (캐시된 레이아웃 사용)
          const container = timelineRightSide.current;
          const scrollContainer = timelineScrollableContainerRef.current;
          let trackTarget: AssetDropTrackTarget = { type: 'create-at-bottom' };

          if (container && scrollContainer) {
            const containerRect = container.getBoundingClientRect();
            const scrollTop = scrollContainer.scrollTop;

            trackTarget = getAssetDropTrackTarget({
              clientY: e.clientY,
              containerRect,
              tracks: staticCache.tracksLayout,
              scrollTop,
            });
          }
          if (isBrandTemplateEditor) {
            frame = 0;
            incomingSnapPoint = null;
            trackTarget = { type: 'create-at-top' };
          }
          const target = captureExternalTarget(tracks, trackTarget);
          const kind =
            dragData.contentType === DtoContentType.audio ? 'audio' : 'video';
          const key = JSON.stringify([
            target,
            kind,
            frame,
            durationInFrames,
            dragData.name,
            base.isLinkingEnabled,
            base.isSnappingEnabled,
            hasPendingTimelineEdit(base),
            estimatedDuration,
            incomingSnapPoint?.frame ?? null,
          ]);
          if (
            lastAssetPreviewRef.current?.key === key &&
            lastAssetPreviewRef.current.document === base.undoableState
          )
            return;
          const planned = previewIncomingPlacement(
            base,
            target,
            kind,
            frame,
            durationInFrames,
          );
          const supported = [
            DtoContentType.audio,
            DtoContentType.video,
            DtoContentType.image,
          ].some((type) => type === dragData.contentType);
          const isAvailable = supported && planned.ok;
          if (target.type === 'new' && planned.index !== null)
            trackTarget = {
              type: 'create-between',
              insertAtIndex: planned.index,
            };
          dragPositionCacheRef.current = {
            baseDocument: base.undoableState,
            baseLinkingEnabled: base.isLinkingEnabled,
            baseSnappingEnabled: base.isSnappingEnabled,
            frame,
            trackTarget,
            isAvailable,
          };

          const trackIndex =
            trackTarget.type === 'existing' ? trackTarget.trackIndex : null;

          lastAssetPreviewRef.current = { key, document: base.undoableState };
          setPreviewState({
            snapPoint: incomingSnapPoint,
            estimatedDuration,
            baseDocument: base.undoableState,
            baseLinkingEnabled: base.isLinkingEnabled,
            baseSnappingEnabled: base.isSnappingEnabled,
            projectedDocument: isAvailable
              ? planned.state.undoableState
              : undefined,
            inserted: isAvailable && planned.inserted,
            frame,
            durationInFrames,
            contentType: dragData.contentType,
            name: dragData.name,
            trackIndex,
            trackTargetType: trackTarget.type,
            isAvailable,
            insertAtIndex:
              trackTarget.type === 'create-between'
                ? trackTarget.insertAtIndex
                : undefined,
          });
        }

        // 트랜지션 드래그 처리
        const transitionDragData = getCurrentTransitionDragData();
        if (transitionDragData) {
          const state = stateAsRef.current;
          const transitions = state.undoableState.transitions;

          // 마우스 Y 위치에서 트랙 인덱스 결정 (캐시된 레이아웃 사용)
          const container = timelineRightSide.current;
          const scrollContainer = timelineScrollableContainerRef.current;

          if (container && scrollContainer) {
            const containerRect = container.getBoundingClientRect();
            const scrollTop = scrollContainer.scrollTop;

            const trackIndex = getTrackIndexFromY(
              e.clientY,
              containerRect,
              staticCache.tracksLayout,
              scrollTop,
              TIMELINE_HEADER_HEIGHT,
            );

            if (trackIndex !== null) {
              // 캐시된 데이터를 사용하여 프레임 계산 (O(1))
              const frame = calculateFrameFromDropPositionCached(
                e.clientX,
                staticCache,
              );

              // 캐시된 정렬 아이템을 사용하여 경계 찾기 (O(N), 정렬 비용 제거)
              const nearestEdge = findNearestTransitionEdgeWithSortedItems({
                frame,
                trackIndex,
                tracks: staticCache.tracksRef,
                sortedItemsByTrack: staticCache.sortedItemsByTrack,
                threshold: 20, // 20프레임 범위 내에서 경계 감지
              });

              if (nearestEdge) {
                // 이미 트랜지션이 있는지 확인
                const hasExisting = Object.values(transitions).some(
                  (t) =>
                    t.sourceItemId === nearestEdge.sourceItemId &&
                    t.targetItemId === nearestEdge.targetItemId,
                );

                // 캐시에 저장
                transitionEdgeCacheRef.current = nearestEdge;

                // 불필요한 setState 방지: 이전 값과 비교
                const lastTransitionPreview = lastTransitionPreviewRef.current;
                if (
                  !lastTransitionPreview ||
                  lastTransitionPreview.edgeFrame !== nearestEdge.edgeFrame ||
                  lastTransitionPreview.trackIndex !== nearestEdge.trackIndex
                ) {
                  lastTransitionPreviewRef.current = {
                    edgeFrame: nearestEdge.edgeFrame,
                    trackIndex: nearestEdge.trackIndex,
                  };
                  setTransitionPreviewState({
                    transitionType: transitionDragData.type,
                    name: transitionDragData.name,
                    edge: nearestEdge,
                    isAvailable: !hasExisting,
                  });
                }
              } else {
                // 경계가 없으면 프리뷰 숨김
                if (transitionEdgeCacheRef.current !== null) {
                  transitionEdgeCacheRef.current = null;
                  lastTransitionPreviewRef.current = null;
                  setTransitionPreviewState(null);
                }
              }
            } else {
              if (transitionEdgeCacheRef.current !== null) {
                transitionEdgeCacheRef.current = null;
                lastTransitionPreviewRef.current = null;
                setTransitionPreviewState(null);
              }
            }
          }
        }

        // 이펙트 드래그 처리
        const animationDragData = getCurrentAnimationDragData();
        if (animationDragData) {
          const state = stateAsRef.current;
          const tracks = state.undoableState.tracks;
          const items = state.undoableState.items;

          // 트랙 레이아웃 계산
          const tracksAndLayout = remainOriginalTrackHeights({
            originalTracks: tracks,
            originalItems: items,
            newTracks: tracks,
            newItems: items,
          });

          // 마우스 Y 위치에서 트랙 인덱스 결정
          const container = timelineRightSide.current;
          const scrollContainer = timelineScrollableContainerRef.current;

          if (container && scrollContainer) {
            const containerRect = container.getBoundingClientRect();
            const scrollTop = scrollContainer.scrollTop;

            const trackIndex = getTrackIndexFromY(
              e.clientY,
              containerRect,
              tracksAndLayout,
              scrollTop,
              TIMELINE_HEADER_HEIGHT,
            );

            if (trackIndex !== null) {
              // 마우스 X 위치에서 프레임 계산
              const frame = calculateFrameFromDropPosition(e.clientX);

              // 해당 트랙에서 마우스 위치의 아이템 찾기
              const track = tracks[trackIndex];
              if (track) {
                // 프레임 위치에 있는 아이템 찾기
                let targetItem = null;
                for (const itemId of track.items) {
                  const item = items[itemId];
                  if (
                    item &&
                    item.from <= frame &&
                    frame < item.from + item.durationInFrames
                  ) {
                    targetItem = item;
                    break;
                  }
                }

                if (targetItem) {
                  // 시각적 아이템에만 이펙트 적용 가능 (audio 제외)
                  const isAvailable = canApplyAnimation(targetItem);

                  // 타임라인 너비 계산
                  const timelineWidth =
                    scrollContainer.scrollWidth -
                    TIMELINE_HORIZONTAL_PADDING * 2;
                  const durationInFrames = getCompositionDuration(
                    items,
                    tracks,
                  );
                  const visibleFrames = getVisibleFrames({
                    fps,
                    totalDurationInFrames: durationInFrames,
                  });

                  // 아이템 위치 계산
                  const itemLeft =
                    (targetItem.from / visibleFrames) * timelineWidth;
                  const itemWidth =
                    (targetItem.durationInFrames / visibleFrames) *
                    timelineWidth;

                  const animationState: AnimationDropPreviewState = {
                    animationType: animationDragData.type,
                    name: animationDragData.name,
                    targetItemId: targetItem.id,
                    trackIndex,
                    isAvailable,
                    itemLeft,
                    itemWidth,
                  };

                  // 캐시에 저장 (onDrop에서 재사용)
                  animationTargetCacheRef.current = animationState;
                  setAnimationPreviewState(animationState);
                } else {
                  // 아이템이 없으면 프리뷰 숨김
                  animationTargetCacheRef.current = null;
                  setAnimationPreviewState(null);
                }
              } else {
                animationTargetCacheRef.current = null;
                setAnimationPreviewState(null);
              }
            } else {
              animationTargetCacheRef.current = null;
              setAnimationPreviewState(null);
            }
          }
        }
      }

      // Canvas에서 effect 드래그 시 드롭 불가능 표시
      if (context === 'canvas') {
        const animationDragData = getCurrentAnimationDragData();
        if (animationDragData) {
          // Canvas에는 effect 드롭 불가능
          e.dataTransfer.dropEffect = 'none';
        }
      }
    },
    [
      context,
      getOrCreateDragStaticCache,
      isBrandTemplateEditor,
      calculateFrameFromDropPositionCached,
      fps,
      setPreviewState,
      setTransitionPreviewState,
      setAnimationPreviewState,
      stateAsRef,
    ],
  );

  const onDragLeave: React.DragEventHandler = useCallback(
    (e) => {
      if (
        e.relatedTarget instanceof Node &&
        e.currentTarget.contains(e.relatedTarget)
      )
        return;
      setIsDragOver(false);
      setIsAudioDragOnCanvas(false);
      setPreviewState(null);
      setTransitionPreviewState(null);
      setAnimationPreviewState(null);
      // 모든 캐시 정리
      dragPositionCacheRef.current = null;
      transitionEdgeCacheRef.current = null;
      animationTargetCacheRef.current = null;
      dragStaticCacheRef.current = null;
      lastAssetPreviewRef.current = null;
      lastTransitionPreviewRef.current = null;
    },
    [setPreviewState, setTransitionPreviewState, setAnimationPreviewState],
  );

  const onDrop: React.DragEventHandler = useCallback(
    async (e) => {
      if (externalDragCancelledRef.current) {
        e.preventDefault();
        return;
      }
      setIsDragOver(false);
      setIsAudioDragOnCanvas(false);
      setPreviewState(null);
      setTransitionPreviewState(null);
      setAnimationPreviewState(null);

      // 이펙트 드롭 처리
      const animationDataStr = e.dataTransfer.getData(ANIMATION_DRAG_TYPE);
      if (animationDataStr && context === 'timeline') {
        e.preventDefault();

        // 캐시된 대상 정보 가져오기
        const cachedAnimationTarget = animationTargetCacheRef.current;
        animationTargetCacheRef.current = null;

        // 드롭 가능한 대상이 없거나 적용 불가능한 경우
        if (!cachedAnimationTarget || !cachedAnimationTarget.isAvailable) {
          setCurrentAnimationDragData(null);
          e.dataTransfer.clearData();
          if (cachedAnimationTarget && !cachedAnimationTarget.isAvailable) {
            toast({
              type: 'error',
              description: '이 아이템에는 효과를 적용할 수 없습니다.',
              toastOptions: { position: 'top-center' },
            });
          }
          return;
        }

        isProcessingDropRef.current = true;

        try {
          const animationData = JSON.parse(animationDataStr);

          // Effect 추가
          addAnimation({
            timelineWriteContext,
            targetItemId: cachedAnimationTarget.targetItemId,
            animationType: animationData.type,
          });

          toast({
            type: 'success',
            description: '효과가 적용되었습니다.',
            toastOptions: { position: 'top-center' },
          });
        } catch (error) {
          console.error('이펙트 드롭 실패:', error);
          toast({
            type: 'error',
            description: '효과 적용에 실패했습니다.',
            toastOptions: { position: 'top-center' },
          });
        } finally {
          isProcessingDropRef.current = false;
          setCurrentAnimationDragData(null);
        }
        e.dataTransfer.clearData();
        return;
      }

      // Canvas에서 effect 드롭 시도 시 무시
      if (animationDataStr && context === 'canvas') {
        setCurrentAnimationDragData(null);
        animationTargetCacheRef.current = null;
        e.dataTransfer.clearData();
        return;
      }

      // 중복 드롭 방지
      if (isProcessingDropRef.current) {
        return;
      }

      // 캐시된 위치 정보 가져오기
      const cachedPosition = dragPositionCacheRef.current;
      dragPositionCacheRef.current = null;

      // 캐시된 트랜지션 경계 정보 가져오기
      const cachedTransitionEdge = transitionEdgeCacheRef.current;
      transitionEdgeCacheRef.current = null;

      // 정적 캐시 정리
      dragStaticCacheRef.current = null;
      lastAssetPreviewRef.current = null;
      lastTransitionPreviewRef.current = null;

      if (!FEATURE_DROP_ASSETS_ON_TIMELINE && context === 'timeline') {
        return;
      }

      if (!FEATURE_DROP_ASSETS_ON_CANVAS && context === 'canvas') {
        return;
      }
      e.preventDefault();

      const { current } = playerRef;
      if (!current) {
        throw new Error('playerRef is null');
      }

      // 트랜지션 드롭 처리
      const transitionDataStr = e.dataTransfer.getData(TRANSITION_DRAG_TYPE);
      if (transitionDataStr && context === 'timeline') {
        // 드롭 가능한 위치가 아닌 경우 (캐시된 edge가 없음)
        if (!cachedTransitionEdge) {
          setCurrentTransitionDragData(null);
          e.dataTransfer.clearData();
          return;
        }

        isProcessingDropRef.current = true;

        try {
          const transitionData = JSON.parse(transitionDataStr);
          const state = stateAsRef.current;
          const transitions = state.undoableState.transitions;

          // 이미 해당 경계에 트랜지션이 있는지 확인
          const hasExisting = Object.values(transitions).some(
            (t) =>
              t.sourceItemId === cachedTransitionEdge.sourceItemId &&
              t.targetItemId === cachedTransitionEdge.targetItemId,
          );

          if (hasExisting) {
            toast({
              type: 'error',
              description: '이미 트랜지션이 적용된 위치입니다.',
              toastOptions: { position: 'top-center' },
            });
          } else {
            // source/target 아이템 가져오기
            const items = state.undoableState.items;
            const assets = state.undoableState.assets;
            const sourceItem = items[cachedTransitionEdge.sourceItemId];
            const targetItem = items[cachedTransitionEdge.targetItemId];

            if (sourceItem && targetItem) {
              // 트랜지션 추가
              addTransition({
                timelineWriteContext,
                transitionType: transitionData.type,
                direction: transitionData.direction,
                edge: cachedTransitionEdge,
                sourceItem,
                targetItem,
                assets,
                fps,
              });

              toast({
                type: 'success',
                description: '트랜지션이 추가되었습니다.',
                toastOptions: { position: 'top-center' },
              });
            } else {
              toast({
                type: 'error',
                description: '연결할 아이템을 찾을 수 없습니다.',
                toastOptions: { position: 'top-center' },
              });
            }
          }
        } catch (error) {
          console.error('트랜지션 드롭 실패:', error);
          toast({
            type: 'error',
            description: '트랜지션 추가에 실패했습니다.',
            toastOptions: { position: 'top-center' },
          });
        } finally {
          isProcessingDropRef.current = false;
          setCurrentTransitionDragData(null);
        }
        e.dataTransfer.clearData();
        return;
      }

      // 내부 에셋 드래그 처리
      const workAssetId = e.dataTransfer.getData(WORK_ASSET_DRAG_TYPE);
      if (workAssetId) {
        try {
          const drag = getWorkAssetDrag();
          const state = stateAsRef.current;
          if (!drag || drag.assetId !== workAssetId || drag.document !== state.undoableState)
            throw new Error('작업 내용이 바뀌었습니다. 자료함에서 다시 드래그해 주세요.');
          if (context === 'timeline' && (!cachedPosition || !cachedPosition.isAvailable || !isIncomingPreviewCurrent(state,cachedPosition)))
            throw new Error('배치할 위치를 확인하지 못했습니다. 원하는 트랙 위로 다시 드래그해 주세요.');
          const target = context === 'timeline' && cachedPosition
            ? captureExternalTarget(state.undoableState.tracks,cachedPosition.trackTarget)
            : captureInsertionTarget(state.undoableState.tracks);
          let position: DropPosition | null = null;
          if (context === 'canvas' && state.undoableState.assets[workAssetId]?.type !== 'audio') {
            const node = current.getContainerNode();
            if (!node || !size) throw new Error('배치할 화면 위치를 확인하지 못했습니다.');
            const rect = node.getBoundingClientRect();
            const scale = calculateScale({canvasSize:size,compositionHeight,compositionWidth,previewSize:previewSize.size});
            if (!Number.isFinite(scale) || scale <= 0) throw new Error('미리보기 배율을 확인하지 못했습니다.');
            position = {x:Math.round((e.clientX-rect.left)/scale),y:Math.round((e.clientY-rect.top)/scale)};
          }
          const prepared = prepareWorkAsset(state.undoableState,workAssetId,crypto.randomUUID(),
            context === 'timeline' && cachedPosition ? cachedPosition.frame : current.getCurrentFrame(),position);
          commitIncomingMedia(timelineWriteContext,prepared.item,prepared.asset,target,state.undoableState.fps);
        } catch(error) {
          toast({type:'error',description:error instanceof Error?error.message:'에셋을 추가하지 못했습니다.'});
        } finally {
          setWorkAssetDrag(null);
          e.dataTransfer.clearData();
        }
        return;
      }
      const assetDataStr = e.dataTransfer.getData(ASSET_DRAG_TYPE);
      const bgmDataStr = e.dataTransfer.getData(BGM_DRAG_TYPE);

      if (bgmDataStr && context === 'canvas') {
        isProcessingDropRef.current = true;

        try {
          const bgmData: BgmDragData = JSON.parse(bgmDataStr);
          const state = stateAsRef.current;
          const tracks = state.undoableState.tracks;
          const blob = await readBgmFile(bgmData.publicUrl,bgmData.wakaBgmSource);

          const timelineStartFrame = isBrandTemplateEditor
            ? 0
            : current.getCurrentFrame();

          await addBgmAssetToTimeline({
            file: blob,
            contentId: bgmData.itemId.toString(),
            wakaBgmSource:bgmData.wakaBgmSource,
            filename: bgmData.name,
            contentPublicUri: bgmData.publicUrl,
            durationInMilliseconds: bgmData.duration,
            timelineWriteContext,
            playerRef,
            fps,
            compositionWidth,
            compositionHeight,
            tracks,
            projectType: resolvedProjectType,
            extension:
              DtoExtensionReverse[
                bgmData.extension as keyof typeof DtoExtensionReverse
              ] ?? null,
            timelineStartFrame,
            placement: {
              type: 'new',
              insertAtIndex: tracks.length,
            },
          });

          toast({
            type: 'success',
            description: '배경음악이 타임라인에 추가되었습니다.',
            toastOptions: { position: 'top-center' },
          });
        } catch (error) {
          console.error('캔버스 BGM 드롭 실패:', error);
          toast({
            type: 'error',
            description: '배경음악 추가에 실패했습니다.',
            toastOptions: { position: 'top-center' },
          });
        } finally {
          isProcessingDropRef.current = false;
          setCurrentBgmDragData(null);
        }
        e.dataTransfer.clearData();
        return;
      }

      // 캔버스에 에셋 드롭 처리
      if (assetDataStr && context === 'canvas') {
        isProcessingDropRef.current = true;

        let assetData: AssetDragData | null = null;
        try {
          assetData = JSON.parse(assetDataStr);
          if (!assetData) {
            throw new Error('assetData parse failed');
          }
          const state = stateAsRef.current;
          const tracks = state.undoableState.tracks;
          const typeMap: Record<DtoContentType, ClipType | null> = {
            [DtoContentType.audio]: CLIP_TYPES.AUDIO,
            [DtoContentType.video]: CLIP_TYPES.VIDEO,
            [DtoContentType.image]: CLIP_TYPES.IMAGE,
            [DtoContentType.font]: null,
            [DtoContentType.document]: null,
          };
          const clipType = typeMap[assetData.contentType];

          if (!clipType) {
            toast({
              type: 'error',
              description: '지원하지 않는 에셋 타입입니다.',
              toastOptions: { position: 'top-center' },
            });
            isProcessingDropRef.current = false;
            e.dataTransfer.clearData();
            return;
          }

          // 오디오는 캔버스에 시각적 요소가 없으므로 현재 playhead 위치에 타임라인에 추가
          const isAudio = assetData.contentType === DtoContentType.audio;

          let canvasDropPosition: DropPosition | null = null;
          if (!isAudio) {
            // 비디오/이미지: 캔버스 드롭 위치 계산
            const containerNode = current.getContainerNode();
            if (!containerNode) {
              throw new Error('containerNode is null');
            }

            const playerRect = containerNode.getBoundingClientRect();
            if (!size) {
              throw new Error('size is null');
            }

            const scale = calculateScale({
              canvasSize: size,
              compositionHeight,
              compositionWidth,
              previewSize: previewSize.size,
            });
            const dropPositionX = Math.round(
              (e.clientX - playerRect.left) / scale,
            );
            const dropPositionY = Math.round(
              (e.clientY - playerRect.top) / scale,
            );
            canvasDropPosition = {
              x: dropPositionX,
              y: dropPositionY,
            };
          }

          // Blob fetch (다운로드 프로그래스 다이얼로그 표시)
          const blob = await fetchAssetWithProgress(
            assetData.publicUrl,
            assetData.name,
            assetData.extension,
          );
          const source = assetData.staticPath
            ? await catalogSoundEffectSource(assetData.staticPath, blob)
            : assetData.publicUrl;
          if (
            assetData.staticPath &&
            (clipType !== CLIP_TYPES.AUDIO ||
              stateAsRef.current.undoableState !== state.undoableState)
          )
            throw new Error('편집 내용이나 효과음 원본이 바뀌었습니다. 다시 드래그해 주세요.');

          // 시작 프레임 결정: 브랜드 템플릿 에디터는 0프레임 고정, 그 외 오디오는 현재 playhead 위치
          const timelineStartFrame = isBrandTemplateEditor
            ? 0
            : isAudio
              ? current.getCurrentFrame()
              : undefined;

          // 에셋 추가
          await addAssetWkwk({
            file: blob,
            contentId: assetData.itemId.toString(),
            filename: assetData.name,
            clip: {
              clipType,
              contentPublicUri: source,
              videoStart: 0,
              videoEnd: assetData.duration ?? 0,
            },
            timelineWriteContext,
            playerRef,
            dropPosition: canvasDropPosition,
            fps,
            compositionWidth,
            compositionHeight,
            tracks,
            projectType: resolvedProjectType,
            isAsset: true,
            extension:
              DtoExtensionReverse[
                assetData.extension as keyof typeof DtoExtensionReverse
              ] ?? null,
            timelineStartFrame, // 브랜드 템플릿: 0, 오디오: 현재 playhead 위치, 비디오/이미지: undefined
            targetTrackIndex: isBrandTemplateEditor ? -1 : undefined, // 자동 배치
            staticPath: assetData.staticPath ?? null, // public 폴더 내 정적 파일 경로
            expectedUndoableState: assetData.staticPath
              ? state.undoableState
              : undefined,
            // 브랜드 템플릿 에디터는 6초 고정
            ...(isBrandTemplateEditor && {
              durationInSeconds: BRAND_TEMPLATE_PREVIEW_DURATION_SECONDS,
            }),
          });

          toast({
            type: 'success',
            description: isAudio
              ? '효과음이 타임라인에 추가되었습니다.'
              : '에셋이 캔버스에 추가되었습니다.',
            toastOptions: { position: 'top-center' },
          });
        } catch (error) {
          console.error(
            '[DropHandler] canvas asset drop addAsset failed:',
            error,
          );
          showUploadFailureToast(
            t,
            classifyUploadFailure(
              error,
              assetData?.name ?? t('Common.upload-fallback-name'),
            ),
          );
        } finally {
          isProcessingDropRef.current = false;
          setCurrentDragData(null);
        }
        e.dataTransfer.clearData();
        return;
      }

      if (bgmDataStr && context === 'timeline') {
        isProcessingDropRef.current = true;

        try {
          const bgmData: BgmDragData = JSON.parse(bgmDataStr);
          const state = stateAsRef.current;
          const tracks = state.undoableState.tracks;

          let startFrame: number;
          let trackTarget: AssetDropTrackTarget;

          if (cachedPosition) {
            if (
              !isIncomingPreviewCurrent(state, cachedPosition) ||
              !cachedPosition.isAvailable
            )
              throw new Error(
                '배치할 수 없는 위치이거나 편집 내용이 바뀌었습니다. 원하는 위치에 다시 놓아 주세요.',
              );
            startFrame = cachedPosition.frame;
            trackTarget = cachedPosition.trackTarget;
          } else {
            const items = state.undoableState.items;
            startFrame = calculateFrameFromDropPosition(e.clientX);

            const tracksAndLayout = remainOriginalTrackHeights({
              originalTracks: tracks,
              originalItems: items,
              newTracks: tracks,
              newItems: items,
            });

            const container = timelineRightSide.current;
            const scrollContainer = timelineScrollableContainerRef.current;
            trackTarget = { type: 'create-at-bottom' };

            if (container && scrollContainer) {
              const containerRect = container.getBoundingClientRect();
              const scrollTop = scrollContainer.scrollTop;

              trackTarget = getAssetDropTrackTarget({
                clientY: e.clientY,
                containerRect,
                tracks: tracksAndLayout,
                scrollTop,
              });
            }
          }

          const blob = await readBgmFile(bgmData.publicUrl,bgmData.wakaBgmSource);

          const audioZoneBoundaryIndex = getZoneBoundaryIndex(tracks);
          const placement =
            trackTarget.type === 'existing'
              ? {
                  type: 'existing' as const,
                  trackIndex: trackTarget.trackIndex,
                }
              : trackTarget.type === 'create-at-top'
                ? {
                    type: 'new' as const,
                    insertAtIndex: audioZoneBoundaryIndex,
                  }
                : trackTarget.type === 'create-between'
                  ? {
                      type: 'new' as const,
                      insertAtIndex: trackTarget.insertAtIndex,
                    }
                  : {
                      type: 'new' as const,
                      insertAtIndex: tracks.length,
                    };

          await addBgmAssetToTimeline({
            file: blob,
            contentId: bgmData.itemId.toString(),
            wakaBgmSource:bgmData.wakaBgmSource,
            filename: bgmData.name,
            contentPublicUri: bgmData.publicUrl,
            durationInMilliseconds: bgmData.duration,
            timelineWriteContext,
            playerRef,
            fps,
            compositionWidth,
            compositionHeight,
            tracks,
            projectType: resolvedProjectType,
            extension:
              DtoExtensionReverse[
                bgmData.extension as keyof typeof DtoExtensionReverse
              ] ?? null,
            timelineStartFrame: isBrandTemplateEditor ? 0 : startFrame,
            placement,
          });

          toast({
            type: 'success',
            description: '배경음악이 타임라인에 추가되었습니다.',
            toastOptions: { position: 'top-center' },
          });
        } catch (error) {
          console.error('BGM 드롭 실패:', error);
          toast({
            type: 'error',
            description: '배경음악 추가에 실패했습니다.',
            toastOptions: { position: 'top-center' },
          });
        } finally {
          isProcessingDropRef.current = false;
          setCurrentBgmDragData(null);
        }
        e.dataTransfer.clearData();
        return;
      }

      // 타임라인에 에셋 드롭 처리
      if (assetDataStr && context === 'timeline') {
        // 드롭 처리 시작
        isProcessingDropRef.current = true;

        let assetData: AssetDragData | null = null;
        try {
          assetData = JSON.parse(assetDataStr);
          if (!assetData) {
            throw new Error('assetData parse failed');
          }
          const state = stateAsRef.current;
          const tracks = state.undoableState.tracks;

          // 캐시된 위치 사용 (없으면 재계산)
          let startFrame: number;
          let trackTarget: AssetDropTrackTarget;

          if (cachedPosition) {
            if (
              !isIncomingPreviewCurrent(state, cachedPosition) ||
              !cachedPosition.isAvailable
            )
              throw new Error(
                '배치할 수 없는 위치이거나 편집 내용이 바뀌었습니다. 원하는 위치에 다시 놓아 주세요.',
              );
            // 캐시된 값 사용 (성능 최적화)
            startFrame = cachedPosition.frame;
            trackTarget = cachedPosition.trackTarget;
          } else {
            // 캐시가 없으면 재계산 (fallback)
            const items = state.undoableState.items;
            startFrame = calculateFrameFromDropPosition(e.clientX);

            const tracksAndLayout = remainOriginalTrackHeights({
              originalTracks: tracks,
              originalItems: items,
              newTracks: tracks,
              newItems: items,
            });

            const container = timelineRightSide.current;
            const scrollContainer = timelineScrollableContainerRef.current;
            trackTarget = { type: 'create-at-bottom' };

            if (container && scrollContainer) {
              const containerRect = container.getBoundingClientRect();
              const scrollTop = scrollContainer.scrollTop;

              trackTarget = getAssetDropTrackTarget({
                clientY: e.clientY,
                containerRect,
                tracks: tracksAndLayout,
                scrollTop,
              });
            }
          }

          // 트랙 타겟 타입에 따른 인덱스 결정
          let targetTrackIndex: number | null = null;
          if (trackTarget.type === 'existing') {
            targetTrackIndex = trackTarget.trackIndex;
          } else if (trackTarget.type === 'create-at-top') {
            targetTrackIndex = -1; // 최상단에 새 트랙 생성
          } else if (trackTarget.type === 'create-at-bottom') {
            targetTrackIndex = tracks.length; // 최하단에 새 트랙 생성
          } else if (trackTarget.type === 'create-between') {
            // 해당 위치에 새 트랙 삽입 (on-track으로 설정하면 겹침 시 해당 위치에 새 트랙 생성)
            targetTrackIndex = trackTarget.insertAtIndex;
          }

          // clipType 매핑
          const typeMap: Record<DtoContentType, ClipType | null> = {
            [DtoContentType.audio]: CLIP_TYPES.AUDIO,
            [DtoContentType.video]: CLIP_TYPES.VIDEO,
            [DtoContentType.image]: CLIP_TYPES.IMAGE,
            [DtoContentType.font]: null,
            [DtoContentType.document]: null,
          };
          const clipType = typeMap[assetData.contentType];

          if (!clipType) {
            toast({
              type: 'error',
              description: '지원하지 않는 에셋 타입입니다.',
              toastOptions: { position: 'top-center' },
            });
            isProcessingDropRef.current = false;
            e.dataTransfer.clearData();
            return;
          }

          // Blob fetch (다운로드 프로그래스 다이얼로그 표시)
          const blob = await fetchAssetWithProgress(
            assetData.publicUrl,
            assetData.name,
            assetData.extension,
          );
          const source = assetData.staticPath
            ? await catalogSoundEffectSource(assetData.staticPath, blob)
            : assetData.publicUrl;
          if (
            assetData.staticPath &&
            (clipType !== CLIP_TYPES.AUDIO ||
              stateAsRef.current.undoableState !== state.undoableState)
          )
            throw new Error('편집 내용이나 효과음 원본이 바뀌었습니다. 다시 드래그해 주세요.');

          // 에셋 추가 (시작 프레임 및 트랙 인덱스 포함)
          // 브랜드 템플릿 에디터는 0프레임부터 시작, 6초 고정
          const timelineStartFrameForAsset = isBrandTemplateEditor
            ? 0
            : startFrame;

          const targetTrackIndexForAsset = isBrandTemplateEditor
            ? -1
            : targetTrackIndex;

          await addAssetWkwk({
            file: blob,
            contentId: assetData.itemId.toString(),
            filename: assetData.name,
            clip: {
              clipType,
              contentPublicUri: source,
              videoStart: 0, // 소스 미디어의 시작점 (0부터 시작)
              videoEnd: assetData.duration ?? 0, // 소스 미디어의 전체 길이
            },
            timelineWriteContext,
            playerRef,
            dropPosition: null,
            fps,
            compositionWidth,
            compositionHeight,
            tracks,
            projectType: resolvedProjectType,
            isAsset: true,
            extension:
              DtoExtensionReverse[
                assetData.extension as keyof typeof DtoExtensionReverse
              ] ?? null,
            timelineStartFrame: timelineStartFrameForAsset, // 브랜드 템플릿: 0, 그 외: 드롭 위치
            targetTrackIndex: targetTrackIndexForAsset, // 드롭된 트랙 인덱스
            createTrack:
              isBrandTemplateEditor || trackTarget.type === 'create-between',
            staticPath: assetData.staticPath ?? null, // public 폴더 내 정적 파일 경로
            expectedUndoableState: assetData.staticPath
              ? state.undoableState
              : undefined,
            // 브랜드 템플릿 에디터는 6초 고정
            ...(isBrandTemplateEditor && {
              durationInSeconds: BRAND_TEMPLATE_PREVIEW_DURATION_SECONDS,
            }),
          });

          toast({
            type: 'success',
            description: '에셋이 타임라인에 추가되었습니다.',
            toastOptions: { position: 'top-center' },
          });
        } catch (error) {
          console.error(
            '[DropHandler] timeline asset drop addAsset failed:',
            error,
          );
          showUploadFailureToast(
            t,
            classifyUploadFailure(
              error,
              assetData?.name ?? t('Common.upload-fallback-name'),
            ),
          );
        } finally {
          // 드롭 처리 완료
          isProcessingDropRef.current = false;
          // 전역 드래그 데이터 정리 (안전장치)
          setCurrentDragData(null);
        }
        e.dataTransfer.clearData();
        return;
      }

      // 외부 파일 드롭 처리 (기존 로직)
      // 로컬 PC에서의 파일 드롭은 차단 (에셋 패널에서만 허용)
      if (e.dataTransfer.files.length > 0 && !assetDataStr) {
        // 로컬 파일 드롭 시도 - 차단 및 에셋 패널 열기
        toast({
          type: 'error',
          description: '파일은 에셋 패널을 통해 업로드해주세요.',
          toastOptions: { position: 'top-center' },
        });
        openAssetPanel();
        e.dataTransfer.clearData();
        return;
      }

      let dropPosition: DropPosition | null = null;
      if (context === 'canvas') {
        const containerNode = current.getContainerNode();
        if (!containerNode) {
          throw new Error('containerNode is null');
        }

        const playerRect = containerNode.getBoundingClientRect();
        if (!size) {
          throw new Error('size is null');
        }

        const scale = calculateScale({
          canvasSize: size,
          compositionHeight,
          compositionWidth,
          previewSize: previewSize.size,
        });
        const dropPositionX = Math.round((e.clientX - playerRect.left) / scale);
        const dropPositionY = Math.round((e.clientY - playerRect.top) / scale);
        dropPosition = { x: dropPositionX, y: dropPositionY };
      } else {
        dropPosition = null;
      }

      const state = stateAsRef.current;
      const tracks = state.undoableState.tracks;
      const stateFps = state.undoableState.fps;

      const uploadPromises = [];
      for (const file of e.dataTransfer.files) {
        uploadPromises.push(
          addAsset({
            file,
            compositionHeight,
            compositionWidth,
            tracks: tracks,
            fps: stateFps,
            timelineWriteContext: timelineWriteContext,
            playerRef,
            dropPosition,
            filename: file.name,
          }),
        );
      }
      await Promise.all(uploadPromises);

      e.dataTransfer.clearData();
    },
    [
      calculateFrameFromDropPosition,
      fetchAssetWithProgress,
      isBrandTemplateEditor,
      resolvedProjectType,
      compositionHeight,
      compositionWidth,
      context,
      fps,
      openAssetPanel,
      playerRef,
      previewSize.size,
      setPreviewState,
      setTransitionPreviewState,
      setAnimationPreviewState,
      size,
      timelineWriteContext,
      stateAsRef,
      t,
    ],
  );

  const style = useMemo(() => {
    if (context === 'canvas') {
      return {
        padding: CANVAS_PADDING,
      };
    }

    return {};
  }, [context]);

  // 드래그 오버 시 스타일 결정
  const dragOverClassName = useMemo(() => {
    if (!isDragOver) return undefined;

    if (context === 'timeline') {
      return 'ring-2 ring-sys-style-primary-default ring-inset';
    }

    if (context === 'canvas') {
      // 오디오 드래그 시 에러 스타일
      if (isAudioDragOnCanvas) {
        return 'ring-2 ring-red-500 ring-inset';
      }
      // 일반 에셋 드래그 시 성공 스타일
      return 'ring-2 ring-sys-style-primary-default ring-inset';
    }

    return undefined;
  }, [isDragOver, context, isAudioDragOnCanvas]);

  return (
    <AbsoluteFill
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={style}
      className={dragOverClassName}
    >
      {children}
    </AbsoluteFill>
  );
};
